import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DefaultResponse } from '@reflact/reflactportal';
import { firstValueFrom } from 'rxjs';
import { CurrentWeather, Tile, TileLayoutItem, TileLayoutState, TileVisibilityDebugEntry, TileWidget } from '../types/tile.types';

interface ListTilesResponse extends DefaultResponse {
  tiles: Tile[];
}

interface ListTilesDebugResponse extends DefaultResponse {
  tiles: TileVisibilityDebugEntry[];
}

interface TileLayoutResponse extends DefaultResponse, Partial<TileLayoutState> {}

interface DefaultTileLayoutResponse extends DefaultResponse {
  items: TileLayoutItem[];
  tiles: Tile[];
}

interface ApplicationsWithoutTileResponse extends DefaultResponse {
  applications: { id: string; name: string }[];
}

/** Eine Person mit individueller Kachelfreigabe (`mandant.userTiles`) in genau einem Mandanten —
 * siehe TileService#listIndividualAccess(). */
export interface TileIndividualAccessGrant {
  userId: string;
  name: string;
  email: string;
  mandantId: string;
  mandantName: string;
}

/** Eine Person mit `@reflact.com`-Adresse, die als Kachelverantwortliche infrage kommt. */
export interface TileResponsibleCandidate {
  userId: string;
  name: string;
  email: string;
}

export type TileResponsibilityTile = Pick<Tile, '_id' | 'name' | 'subtext' | 'color' | 'icon' | 'kind' | 'responsibleUserId'>;

interface TileResponsibilityResponse extends DefaultResponse {
  tiles: TileResponsibilityTile[];
  candidates: TileResponsibleCandidate[];
}

interface IndividualAccessResponse extends DefaultResponse {
  grants: TileIndividualAccessGrant[];
}

@Injectable({
  providedIn: 'root'
})
export class TileService {

  constructor(public http: HttpClient) { }

  public async saveWebTile(tile: Tile, scope: 'global' | 'tenant' | 'personal'): Promise<DefaultResponse> {
    const prefix = TileService.webTilePrefix(scope);
    return await firstValueFrom(this.http.post<DefaultResponse>(`${prefix}/${tile._id ? 'edit' : 'create'}`, { ...tile, kind: 'link' }));
  }

  public async deleteWebTile(tileId: string, scope: 'global' | 'tenant' | 'personal'): Promise<DefaultResponse> {
    const prefix = TileService.webTilePrefix(scope);
    return await firstValueFrom(this.http.post<DefaultResponse>(`${prefix}/delete`, { tileId }));
  }

  private static webTilePrefix(scope: 'global' | 'tenant' | 'personal'): string {
    return scope === 'global' ? '/api/tile' : `/api/tile/${scope === 'tenant' ? 'tenant' : 'personal'}`;
  }

  /** Eigene, private Weblink-Kacheln des Nutzers — mandantenunabhängig und nur für ihn sichtbar. */
  public async listPersonalTiles(): Promise<Tile[]> {
    const res = await firstValueFrom(this.http.get<ListTilesResponse>('/api/tile/personal'));
    return res.status == 'ok' ? res.tiles : [];
  }

  /** Legt eine eingebaute private Kachel (Notiz, Uhr, Wetter) an oder ändert sie. */
  public async savePersonalWidget(tileId: string, name: string, widget: TileWidget): Promise<DefaultResponse> {
    const action = tileId ? 'edit' : 'create';
    return await firstValueFrom(this.http.post<DefaultResponse>(`/api/tile/personal/${action}`, { _id: tileId || undefined, name, widget }));
  }

  /** Speichert nur den Text einer Notiz-Kachel. */
  public async saveNote(tileId: string, text: string): Promise<DefaultResponse> {
    return await firstValueFrom(this.http.post<DefaultResponse>('/api/tile/personal/note', { tileId, text }));
  }

  public async getWeather(postalCode: string): Promise<CurrentWeather> {
    const res = await firstValueFrom(this.http.get<DefaultResponse & { weather: CurrentWeather }>('/api/tile/weather', { params: { postalCode } }));
    if (res.status !== 'ok') throw new Error(res.message);
    return res.weather;
  }

  public async getWebsiteMetadata(url: string): Promise<Pick<Tile, 'name' | 'subtext'> & Partial<Pick<Tile, 'color' | 'imageUrl'>>> {
    const response = await firstValueFrom(this.http.post<DefaultResponse & { metadata: Pick<Tile, 'name' | 'subtext'> & Partial<Pick<Tile, 'color' | 'imageUrl'>> }>('/api/tile/websiteMetadata', { url }));
    if (response.status !== 'ok') throw new Error(response.message);
    return response.metadata;
  }

  public async listTiles(): Promise<Tile[]> {
    const res = await firstValueFrom(this.http.get<ListTilesResponse>("/api/tile/list"))
    if (res.status == "ok") {
      return res.tiles;
    }
    return [];
  }

  /** Eigene Weblink-Kacheln des aktuellen Mandanten, ungefiltert (im Gegensatz zu listMyTiles
   * unabhängig von der individuellen Sichtbarkeit des aufrufenden Nutzers) — Basis für die
   * Verwaltungs-Tabelle, damit ein Mandanten-Admin seine eigenen Kacheln auch dann noch verwalten
   * kann, wenn er sich selbst (versehentlich) von der Sichtbarkeit ausgeschlossen hat. */
  public async listTenantTiles(): Promise<Tile[]> {
    const res = await firstValueFrom(this.http.get<ListTilesResponse>("/api/tile/tenant"));
    if (res.status == "ok") {
      return res.tiles;
    }
    return [];
  }

  public async listMyTiles(): Promise<Tile[]> {
    const res = await firstValueFrom(this.http.get<ListTilesResponse>("/api/tile/listMy"));
    if (res.status == "ok") {
      return res.tiles;
    }
    return [];
  }

  /** Persönliches Bento-Layout und Standardlayout des aktuell ausgewählten Mandanten. */
  public async getTileLayout(): Promise<TileLayoutState> {
    const res = await firstValueFrom(this.http.get<TileLayoutResponse>('/api/tile/layout'));
    if (res.status != 'ok') return { items: [], personal: false, defaultItems: [] };
    return { items: res.items ?? [], personal: res.personal ?? !!res.items?.length, defaultItems: res.defaultItems ?? [] };
  }

  /** Standardlayout des Mandanten und die Kacheln, die alle Nutzer sehen (nur Mandanten- und Super-Admins). */
  public async getDefaultTileLayout(): Promise<{ items: TileLayoutItem[]; tiles: Tile[] }> {
    const res = await firstValueFrom(this.http.get<DefaultTileLayoutResponse>('/api/tile/default-layout'));
    if (res.status != 'ok') throw new Error(res.message || 'Standardlayout nicht verfügbar');
    return { items: res.items, tiles: res.tiles };
  }

  public async saveDefaultTileLayout(items: TileLayoutItem[]): Promise<DefaultResponse> {
    return await firstValueFrom(this.http.post<DefaultResponse>('/api/tile/default-layout/save', { items }));
  }

  public async resetDefaultTileLayout(): Promise<DefaultResponse> {
    return await firstValueFrom(this.http.post<DefaultResponse>('/api/tile/default-layout/reset', {}));
  }

  public async saveTileLayout(items: TileLayoutItem[]): Promise<DefaultResponse> {
    return await firstValueFrom(this.http.post<DefaultResponse>('/api/tile/layout/save', { items }));
  }

  public async resetTileLayout(): Promise<DefaultResponse> {
    return await firstValueFrom(this.http.post<DefaultResponse>('/api/tile/layout/reset', {}));
  }

  /** Versteckte Debug-Ansicht (Strg+Alt+D im Portal) — im Gegensatz zu listMyTiles auch Kacheln,
   * die der Organisation zugewiesen sind aber wegen fehlender Rollen-Berechtigung nicht sichtbar sind. */
  public async listMyTilesDebug(): Promise<TileVisibilityDebugEntry[]> {
    const res = await firstValueFrom(this.http.get<ListTilesDebugResponse>("/api/tile/listMyDebug"));
    if (res.status == "ok") {
      return res.tiles;
    }
    return [];
  }

  /** Applications, die noch nicht als Kachel verwendet werden — Basis für den "mit bestehender
   * Application verknüpfen"-Picker beim Anlegen einer `kind: "application"`-Kachel. */
  public async listApplicationsWithoutTile(): Promise<{ id: string; name: string }[]> {
    const res = await firstValueFrom(this.http.get<ApplicationsWithoutTileResponse>("/api/tile/applicationsWithoutTile"));
    if (res.status == "ok") {
      return res.applications;
    }
    return [];
  }

  /** `text` (Kachel-Überschrift) wird serverseitig immer aus dem Logto-Application-Namen abgeleitet,
   * `subtext` fließt in Logtos native `description` statt in unsere eigene Kachel-Config —
   * siehe server/src/tile/tile-logto.service.ts. `applicationId` ist nur bei `kind: "application"`
   * nötig (echte, bereits existierende Logto-Application, mit der die Kachel verknüpft wird). */
  public async createTile(name: string, subtext: string, url: string, openInNewTab: boolean, color: string, icon: string, permissions: string[], kind: 'application' | 'link', applicationId?: string): Promise<DefaultResponse> {
    const req: Omit<Tile, "_id"> & { applicationId?: string } = {
      name,
      text: name,
      subtext,
      url,
      openInNewTab,
      color,
      icon,
      permissions,
      kind,
      applicationId,
    }
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/tile/create", req));
    return res;
  }

  public async editTile(tile: Tile): Promise<DefaultResponse> {
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/tile/edit", tile));
    return res;
  }

  public async linkTileToApplication(tileId: string, applicationId: string): Promise<DefaultResponse> {
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/tile/linkToApplication", { tileId, applicationId }));
    return res;
  }

  public async deleteTile(tileId: string): Promise<DefaultResponse> {
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/tile/delete", { tileId }));
    return res;
  }

  /** Setzt, ob eine eigene Weblink-Kachel für alle Mitarbeiter des Mandanten, nur für die
   * gewählten Personen, oder für niemanden sichtbar ist — unabhängig vom Inhalt der Kachel
   * (siehe saveWebTile), da diese Freigabe direkt in der Kachel selbst gespeichert wird. */
  public async setLinkVisibility(tileId: string, mode: 'all' | 'selected' | 'off', userIds?: string[]): Promise<DefaultResponse> {
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/tile/tenant/setVisibility", { tileId, mode, userIds }));
    return res;
  }

  /** Personen (über alle Mandanten hinweg), die diese Kachel individuell zugewiesen bekommen
   * haben — Basis für die "Personen mit Zugriff"-Liste im Info-Tab der Kachelverwaltung. */
  public async listIndividualAccess(tileId: string): Promise<TileIndividualAccessGrant[]> {
    const res = await firstValueFrom(this.http.get<IndividualAccessResponse>(`/api/tile/${tileId}/individualAccess`));
    if (res.status == "ok") {
      return res.grants;
    }
    return [];
  }


  /** Globale Kacheln mit verantwortlicher Person und alle zulässigen Personen (Superadmin). */
  public async getResponsibility(): Promise<{ tiles: TileResponsibilityTile[]; candidates: TileResponsibleCandidate[] }> {
    const res = await firstValueFrom(this.http.get<TileResponsibilityResponse>('/api/tile/responsibility'));
    return res.status == 'ok' ? { tiles: res.tiles, candidates: res.candidates } : { tiles: [], candidates: [] };
  }

  /** `userId: null` entfernt die Verantwortung. */
  public async setResponsible(tileId: string, userId: string | null): Promise<DefaultResponse> {
    return await firstValueFrom(this.http.post<DefaultResponse>('/api/tile/setResponsible', { tileId, userId }));
  }
}
